import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { PageHeader } from "@/components/page-header";
import { DashboardSection } from "@/components/dashboard-section";
import { AddAccountButton } from "@/components/add-account/add-account-button";
import { Button } from "@/components/ui/button";
import { CoolIcon } from "@/components/ui/cool-icon";
import { Loader2, Server, ShieldAlert } from "@/components/ui/icons";
import { firmLogoUrl } from "@/lib/firm-logos";
import { listAccounts, decodeAccountInfo, type AccountRow } from "@/lib/api/accounts";
import { listAccountPairs, type AccountPairListItem } from "@/lib/api/account-pairs";
import { listFirms, type FirmRow } from "@/lib/api/firms";
import { isFuturesPlatform } from "@/lib/market";
import { fmtMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

function useFuturesAccounts(): {
  accounts: AccountRow[] | null;
  error: string | null;
  refresh(): void;
} {
  const [accounts, setAccounts] = useState<AccountRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    setError(null);
    listAccounts()
      .then((rows) => setAccounts(rows.filter((row) => isFuturesPlatform(row.platform_type))))
      .catch((err) => {
        setAccounts([]);
        setError(err instanceof Error ? err.message : String(err));
      });
  };

  useEffect(refresh, []);
  return { accounts, error, refresh };
}

function isFuturesPartnerSet(pair: AccountPairListItem): boolean {
  return (
    isFuturesPlatform(pair.longAccount.platformType) ||
    isFuturesPlatform(pair.shortAccount.platformType)
  );
}

function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <DashboardSection>
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
          <CoolIcon name="Chart_Bar_Vertical_01" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="mt-1 max-w-md text-xs text-muted-foreground">{body}</p>
        </div>
        {action}
      </div>
    </DashboardSection>
  );
}

export function FuturesAccountsPage(): ReactNode {
  const { accounts, error, refresh } = useFuturesAccounts();
  const active = accounts?.filter((row) => row.outcome == null).length ?? 0;
  const funded = accounts?.filter((row) => row.phase === "funded" && row.outcome == null).length ?? 0;
  const breached = accounts?.filter((row) => row.outcome === "breached").length ?? 0;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Futures Accounts"
        subtitle={`${active} Active · ${funded} Funded · ${breached} Breached`}
        icon={<CoolIcon name="Credit_Card_02" />}
        accentWords={["Futures"]}
        right={<AddAccountButton label="Add Futures Account" marketType="futures" onComplete={refresh} />}
      />

      {error && (
        <DashboardSection>
          <p className="text-sm text-destructive">Failed To Load Futures Accounts: {error}</p>
        </DashboardSection>
      )}

      {accounts === null ? (
        <DashboardSection>
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin text-accent" />
            Loading Futures Accounts…
          </div>
        </DashboardSection>
      ) : accounts.length === 0 ? (
        <EmptyState
          title="No Futures Accounts Yet"
          body="Add tomorrow's futures evaluation accounts here. This flow filters the account wizard to futures firms only."
          action={<AddAccountButton label="Add Futures Account" marketType="futures" onComplete={refresh} />}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {accounts.map((row, index) => {
            const info = decodeAccountInfo(row);
            const balance = row.final_balance ?? info?.balance ?? row.account_size ?? 0;
            return (
              <motion.div
                key={row.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.03, 0.2), duration: 0.25 }}
                className="rounded-xl border border-border bg-card/70 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <img
                      src={firmLogoUrl(row.firm_id) ?? ""}
                      alt=""
                      className="h-9 w-9 rounded-lg border border-border object-contain p-1"
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground">
                        {row.account_label || row.login || row.id}
                      </p>
                      <p className="font-mono text-[11px] text-muted-foreground">
                        {row.firm_id} · {row.platform_type}
                      </p>
                    </div>
                  </div>
                  <span
                    className={cn(
                      "rounded-md px-2 py-1 text-[10px] font-semibold capitalize",
                      row.outcome === "breached"
                        ? "bg-destructive/10 text-destructive"
                        : "bg-accent/10 text-accent",
                    )}
                  >
                    {row.outcome ?? row.phase}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                  <Metric label="Balance" value={fmtMoney(balance)} />
                  <Metric label="MLL" value={row.max_drawdown_pct == null ? "Set Rules" : `${row.max_drawdown_pct}%`} />
                  <Metric label="Consistency" value={row.consistency_rule_pct == null ? "40%" : `${row.consistency_rule_pct}%`} />
                </div>
                <Link
                  to={`/accounts/${encodeURIComponent(row.slug)}`}
                  className="mt-4 inline-flex text-xs font-medium text-accent hover:underline"
                >
                  Open Account
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="rounded-lg bg-muted/40 px-2 py-2">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 truncate font-mono text-xs font-semibold text-foreground">{value}</p>
    </div>
  );
}

export function FuturesPartnerSetsPage(): ReactNode {
  const [sets, setSets] = useState<AccountPairListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listAccountPairs("all")
      .then((rows) => setSets(rows.filter(isFuturesPartnerSet)))
      .catch((err) => {
        setSets([]);
        setError(err instanceof Error ? err.message : String(err));
      });
  }, []);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Futures Partner Sets"
        subtitle="Side-Agnostic Account Relationships For Futures Evaluation And Replacement Workflows."
        icon={<CoolIcon name="Arrow_Left_Right" />}
        accentWords={["Futures"]}
        right={<Button render={<Link to="/futures/accounts" />}>Add Accounts First</Button>}
      />
      {error && (
        <DashboardSection>
          <p className="text-sm text-destructive">Failed To Load Partner Sets: {error}</p>
        </DashboardSection>
      )}
      {sets === null ? (
        <DashboardSection>
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin text-accent" />
            Loading Partner Sets…
          </div>
        </DashboardSection>
      ) : sets.length === 0 ? (
        <EmptyState
          title="No Futures Partner Sets Yet"
          body="Futures partner sets are not long/short pairs. They track which accounts are grouped for hedging, replacement, payout, and breach workflows."
          action={<Button render={<Link to="/futures/accounts" />}>Add Futures Accounts</Button>}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {sets.map((set) => (
            <Link
              key={set.id}
              to={`/futures/terminal/${encodeURIComponent(set.slug)}`}
              className="rounded-xl border border-border bg-card/70 p-4 transition-colors hover:border-accent/40 hover:bg-accent/5"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="font-mono text-sm font-semibold text-foreground">
                  {set.symbolAllowlist[0] ?? "Futures"}
                </p>
                <span className="rounded-md bg-muted px-2 py-1 text-[10px] font-semibold capitalize text-muted-foreground">
                  {set.status}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Partner label="Partner A" name={set.longAccount.firmName} platform={set.longAccount.platformType} />
                <Partner label="Partner B" name={set.shortAccount.firmName} platform={set.shortAccount.platformType} />
              </div>
              <p className="mt-3 font-mono text-xs text-muted-foreground">
                Net P&L {set.netPnl >= 0 ? "+" : ""}{fmtMoney(set.netPnl)}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Partner({
  label,
  name,
  platform,
}: {
  label: string;
  name: string;
  platform: string;
}): ReactNode {
  return (
    <div className="rounded-lg bg-muted/40 px-2 py-2">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-xs font-medium text-foreground">{name}</p>
      <p className="font-mono text-[10px] text-muted-foreground">{platform}</p>
    </div>
  );
}

export function FuturesPlatformsPage(): ReactNode {
  const [firms, setFirms] = useState<FirmRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listFirms()
      .then((rows) => setFirms(rows.filter((firm) => (firm.market_type ?? "forex_cfd") === "futures")))
      .catch((err) => {
        setFirms([]);
        setError(err instanceof Error ? err.message : String(err));
      });
  }, []);

  const platforms = useMemo(() => {
    const counts = new Map<string, number>();
    for (const firm of firms ?? []) {
      for (const platform of firm.supported_platforms ?? [firm.platform_type]) {
        counts.set(platform, (counts.get(platform) ?? 0) + 1);
      }
    }
    return Array.from(counts.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [firms]);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Futures Platforms"
        subtitle="Tradovate, NinjaTrader, BlackArrow, And TradingView Bot Targets Live Here."
        icon={<Server className="size-5" />}
        accentWords={["Futures"]}
      />
      {error && (
        <DashboardSection>
          <p className="text-sm text-destructive">Failed To Load Platforms: {error}</p>
        </DashboardSection>
      )}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {platforms.map(([platform, count]) => (
          <DashboardSection key={platform} className="min-h-[130px]">
            <p className="text-sm font-semibold capitalize text-foreground">{platform}</p>
            <p className="mt-1 text-xs text-muted-foreground">{count} Futures Firm{count === 1 ? "" : "s"}</p>
            <p className="mt-4 text-[11px] text-muted-foreground">
              Bot driver and terminal URL rules will be managed independently from FX/CFD.
            </p>
          </DashboardSection>
        ))}
        <DashboardSection className="min-h-[130px] border-dashed">
          <p className="text-sm font-semibold text-foreground">TradingView</p>
          <p className="mt-1 text-xs text-muted-foreground">Planned Platform</p>
          <p className="mt-4 text-[11px] text-muted-foreground">
            Needs broker-connection metadata before execution bots can be reliable.
          </p>
        </DashboardSection>
      </div>
    </div>
  );
}

export function FuturesRiskPage(): ReactNode {
  const { accounts } = useFuturesAccounts();
  const rows = accounts ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Futures Risk"
        subtitle="Payout Buffer, Locked MLL, EOD Trailing Drawdown, And Consistency Rules Belong Here."
        icon={<ShieldAlert className="size-5" />}
        accentWords={["Risk"]}
      />
      <DashboardSection>
        <div className="grid gap-3 md:grid-cols-3">
          <Metric label="Accounts Tracked" value={String(rows.length)} />
          <Metric label="Default Consistency" value="40%" />
          <Metric label="Safe Payout Formula" value="Balance - MLL - Buffer" />
        </div>
        <div className="mt-5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-600 dark:text-amber-400">
          Futures funded payouts must preserve room above locked Maximum Loss Limit.
          This page is the future home for safe withdrawal and post-payout buffer automation.
        </div>
      </DashboardSection>
    </div>
  );
}

export function FuturesBotsPage(): ReactNode {
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Futures Bots"
        subtitle="Separate Bot Fleet For Tradovate, NinjaTrader, BlackArrow, And TradingView Execution."
        icon={<Server className="size-5" />}
        accentWords={["Futures"]}
        right={<Button render={<Link to="/bots" />}>Open Shared Bot Fleet</Button>}
      />
      <DashboardSection>
        <div className="grid gap-3 md:grid-cols-2">
          {["Tradovate", "NinjaTrader", "BlackArrow", "TradingView"].map((platform) => (
            <div key={platform} className="rounded-xl border border-border/60 bg-background/50 p-4">
              <p className="text-sm font-semibold text-foreground">{platform}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Driver ownership, login flow, account switching, and order execution will be isolated from FX/CFD bots.
              </p>
            </div>
          ))}
        </div>
      </DashboardSection>
    </div>
  );
}
